/**
 * A idade de um dado projetado, em português (H03, §14.3).
 *
 * ── Por que não mostrar o carimbo cru ─────────────────────────────────────
 *
 * A §14.3 pede que a tela informe a idade do dado. `2026-08-18T13:02:44.318Z`
 * cumpre a letra e falha no propósito: o atendente precisa saber se o que ele
 * está lendo é de agora ou de meia hora atrás, e ler isso de um ISO exige
 * comparar com o relógio de cabeça, no meio de um atendimento.
 *
 * "há 6 minutos" responde a pergunta que a pessoa realmente tem.
 *
 * ── O carimbo exato não some ──────────────────────────────────────────────
 *
 * Ele vai para o `title` do elemento e para o `datetime` do `<time>`. Quem está
 * investigando um incidente precisa do instante preciso; quem está atendendo,
 * não. Os dois cabem.
 */

const MINUTO = 60_000;
const HORA = 60 * MINUTO;
const DIA = 24 * HORA;

/**
 * `Intl.RelativeTimeFormat` faz o trabalho de plural e concordância.
 *
 * Escrever `há ${n} minuto${n > 1 ? 's' : ''}` funciona em português e quebra
 * na primeira tradução — e a §19 já pede acessibilidade, que anda junto com
 * internacionalização mais cedo do que se espera.
 */
const formatador = new Intl.RelativeTimeFormat('pt-BR', { numeric: 'auto' });

/**
 * Texto curto para a faixa de defasagem.
 *
 * `null` quando não há carimbo — e aí a tela diz "de quando não sei", que é
 * pior que uma idade e melhor que fingir frescor, como a §14.3 exige.
 */
export function idadeEmPalavras(
  projetadoEm: string | null | undefined,
  agora = Date.now(),
): string | null {
  if (!projetadoEm) return null;

  const instante = Date.parse(projetadoEm);
  if (Number.isNaN(instante)) return null;

  const atras = agora - instante;

  // Carimbo no futuro é relógio dessincronizado entre servidor e navegador, e
  // acontece. "em 3 minutos" ao lado de "dado defasado" faria o usuário
  // desconfiar da tela inteira; "agora mesmo" é a leitura honesta.
  if (atras < MINUTO) return 'agora mesmo';

  if (atras < HORA) return formatador.format(-Math.floor(atras / MINUTO), 'minute');
  if (atras < DIA) return formatador.format(-Math.floor(atras / HORA), 'hour');
  return formatador.format(-Math.floor(atras / DIA), 'day');
}

/** O instante exato, para o `title` e para quem investiga incidente. */
export function instantePorExtenso(projetadoEm: string | null | undefined): string | null {
  if (!projetadoEm) return null;
  const instante = new Date(projetadoEm);
  if (Number.isNaN(instante.getTime())) return null;

  return new Intl.DateTimeFormat('pt-BR', {
    dateStyle: 'short',
    timeStyle: 'medium',
  }).format(instante);
}

/**
 * Quanto tempo desde um instante, sem o "há" (28/09/2026).
 *
 * É o texto da tag de etapa do card do funil: "3 h nesta etapa", "6 dias
 * nesta etapa". `idadeEmPalavras` responde "quando?"; esta responde "há quanto
 * tempo está assim?", e "há 6 dias nesta etapa" não é português.
 *
 * `null` quando não há carimbo, como a irmã.
 */
export function duracaoEmPalavras(
  desde: string | null | undefined,
  agora = Date.now(),
): string | null {
  if (!desde) return null;

  const instante = Date.parse(desde);
  if (Number.isNaN(instante)) return null;

  // O mesmo relógio dessincronizado de `idadeEmPalavras`: futuro vira zero.
  const passou = Math.max(0, agora - instante);

  if (passou < HORA) return `${Math.max(1, Math.floor(passou / MINUTO))} min`;
  if (passou < DIA) return `${Math.floor(passou / HORA)} h`;
  const dias = Math.floor(passou / DIA);
  return dias === 1 ? '1 dia' : `${dias} dias`;
}
